import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardContent } from "@learn-me/ui";
import { FadeIn, StaggeredGrid } from "@/components/animated";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";
import {
  Target,
  TrendingUp,
  Clock,
  Briefcase,
  ArrowRight,
  CheckCircle,
  Crosshair,
  Play,
  Shield,
  RefreshCw,
  Eye,
  Compass,
  Linkedin,
  Users,
} from "lucide-react";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  title: string;
  photoUrl: string | null;
  bio: string;
  linkedInUrl: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  ACADEMIC_DIRECTOR: "Director Académico",
  EXECUTIVE_DIRECTOR: "Director Ejecutivo",
  INSTRUCTOR: "Instructor",
  OTHER: "Equipo",
};

const pillars = [
  {
    icon: Target,
    title: "Aprendizaje con propósito y dirección",
    description:
      "Cada programa está diseñado con objetivos claros y medibles. No capacitamos por capacitar: formamos con una meta concreta que genera impacto real en el desempeño.",
  },
  {
    icon: TrendingUp,
    title: "Ejecución orientada a resultados",
    description:
      "El conocimiento sin acción no genera valor. Nuestros programas están estructurados para que los participantes apliquen lo aprendido de manera inmediata y tangible.",
  },
  {
    icon: Clock,
    title: "Disciplina, enfoque y mejora continua",
    description:
      "Promovemos hábitos de aprendizaje sostenibles. Entendemos que el desarrollo profesional es un proceso continuo que requiere compromiso y constancia.",
  },
  {
    icon: Briefcase,
    title: "Desarrollo aplicado al contexto real",
    description:
      "Nuestros contenidos están alineados con las necesidades reales del negocio. Trabajamos casos prácticos y situaciones del día a día laboral.",
  },
];

const values = [
  {
    icon: Crosshair,
    title: "Enfoque",
    description: "Priorizamos lo que genera impacto.",
  },
  {
    icon: Play,
    title: "Ejecución",
    description: "Llevamos el aprendizaje a la práctica.",
  },
  {
    icon: Shield,
    title: "Disciplina",
    description: "La constancia sostiene el crecimiento.",
  },
  {
    icon: CheckCircle,
    title: "Responsabilidad",
    description: "El desarrollo es un compromiso.",
  },
  {
    icon: RefreshCw,
    title: "Mejora continua",
    description: "Evolucionar es parte del proceso.",
  },
];

const differentiators = [
  "Formación alineada con objetivos de negocio",
  "Metodología práctica y aplicable",
  "Seguimiento y medición de resultados",
  "Contenido actualizado por expertos de la industria",
  "Acompañamiento personalizado",
  "Certificaciones reconocidas",
];

// Reactivada el 2026-08-31 a solicitud de la Dirección Académica. Los
// integrantes y sus fotos se administran en /admin/team.
const SHOW_TEAM_SECTION = true;

export default function QuienesSomosPage() {
  const { data: teamMembers } = useQuery({
    queryKey: ["public-team-members"],
    queryFn: async () => {
      const response = await api.get<ApiResponse<TeamMember[]>>("/team");
      return response.data.data || [];
    },
    enabled: SHOW_TEAM_SECTION,
  });

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>

        <div className="container relative z-10">
          <FadeIn>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-6 backdrop-blur-sm">
                Quiénes somos
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Somos la formación que genera{" "}
                <span className="text-primary-400">resultados</span>
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                En ARCUS Learning impulsamos el crecimiento de personas y
                organizaciones a través de aprendizaje con dirección clara y
                enfoque en resultados.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Mission, Vision, Philosophy */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <FadeIn delay={0}>
                <div className="text-center p-8 rounded-2xl bg-primary-50 border border-primary-100">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-600 text-white mb-6">
                    <Target className="h-7 w-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-secondary-900 mb-4">
                    Misión
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    Impulsar el desarrollo de personas y equipos mediante
                    aprendizaje aplicado, enfocado en la ejecución y en la
                    obtención de resultados concretos.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.1}>
                <div className="text-center p-8 rounded-2xl bg-secondary-50 border border-secondary-100">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary-900 text-white mb-6">
                    <Eye className="h-7 w-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-secondary-900 mb-4">
                    Visión
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    Ser un referente en formación estratégica que transforma el
                    aprendizaje en acción, eficiencia y resultados sostenibles.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2}>
                <div className="text-center p-8 rounded-2xl bg-gold-50 border border-gold-100">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold-500 text-white mb-6">
                    <Compass className="h-7 w-7" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-secondary-900 mb-4">
                    Filosofía
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    Creemos que el aprendizaje genera impacto real cuando se
                    convierte en acción, enfoque y disciplina, alineado a
                    objetivos claros y a la realidad del trabajo.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Nuestra Propuesta */}
      <section className="py-20 bg-secondary-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <FadeIn>
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                  Nuestra propuesta
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Formación que transforma
                </h2>
              </div>
              <div className="prose prose-lg max-w-none bg-white rounded-2xl p-8 md:p-12 shadow-sm">
                <p className="text-secondary-600 text-lg leading-relaxed mb-6">
                  En ARCUS Learning impulsamos el crecimiento de personas y
                  organizaciones a través de aprendizaje con dirección clara y
                  enfoque en resultados. Creemos que el desarrollo real ocurre
                  cuando el conocimiento se transforma en acción, disciplina y
                  mejora continua.
                </p>
                <p className="text-secondary-600 text-lg leading-relaxed mb-6">
                  Nuestro enfoque va más allá de la capacitación tradicional.
                  Trabajamos con líderes y equipos que entienden que evolucionar
                  implica reconocer oportunidades de mejora, optimizar el uso
                  del tiempo y ejecutar con claridad y compromiso.
                </p>
                <p className="text-secondary-600 text-lg leading-relaxed mb-6">
                  Acompañamos procesos de formación orientados a objetivos
                  concretos, alineando el aprendizaje con las metas del negocio
                  para generar impacto sostenible y medible.
                </p>
                <p className="text-secondary-600 text-lg leading-relaxed">
                  Arcus Learning es una empresa costarricense con 15 años de
                  trayectoria, especializada en el desarrollo de soluciones
                  tecnológicas y formativas para organizaciones. Formamos parte
                  de Corporación SPI. Combinamos tecnología, conocimiento
                  especializado y diseño de experiencias para crear soluciones
                  que se adaptan a la realidad, estructura y objetivos de cada
                  cliente.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                Lo que nos define
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                Nuestros valores
              </h2>
              <p className="text-secondary-600 max-w-2xl mx-auto">
                Los principios que guían cada decisión y cada programa que
                desarrollamos
              </p>
            </div>
          </FadeIn>

          <div className="max-w-4xl mx-auto">
            <StaggeredGrid
              className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6"
              staggerDelay={0.1}
            >
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="text-center p-6 rounded-xl bg-secondary-50 hover:bg-primary-50 transition-colors group"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-sm text-primary-600 mb-4 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                    <value.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-secondary-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-secondary-600">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </StaggeredGrid>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="py-20 bg-secondary-50">
        <div className="container">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                Nuestro enfoque
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                Pilares que nos definen
              </h2>
              <p className="text-secondary-600 max-w-2xl mx-auto">
                Cada aspecto de nuestra metodología está diseñado para maximizar
                el impacto y generar resultados medibles
              </p>
            </div>
          </FadeIn>

          <StaggeredGrid
            className="grid md:grid-cols-2 gap-8"
            staggerDelay={0.1}
          >
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-100 text-primary-600 mb-6">
                  <pillar.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {pillar.title}
                </h3>
                <p className="text-secondary-600 leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </StaggeredGrid>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn direction="left">
              <div>
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                  Por qué elegirnos
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Formación con impacto real
                </h2>
                <p className="text-secondary-600 text-lg leading-relaxed mb-8">
                  No ofrecemos capacitación genérica. Cada programa está
                  diseñado para generar resultados concretos y medibles en el
                  desempeño de personas y equipos.
                </p>
                <Link to="/courses">
                  <Button size="lg" className="group">
                    Explorar nuestros cursos
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </FadeIn>

            <FadeIn direction="right" delay={0.2}>
              <div className="bg-secondary-50 rounded-2xl p-8">
                <ul className="space-y-4">
                  {differentiators.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
                      <span className="text-secondary-700 font-medium">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {SHOW_TEAM_SECTION && teamMembers && teamMembers.length > 0 && (
        <section className="py-20 bg-secondary-50">
          <div className="container">
            <FadeIn>
              <div className="text-center mb-16">
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                  Nuestro equipo
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                  El equipo detrás de Arcus
                </h2>
                <p className="text-secondary-600 max-w-2xl mx-auto">
                  Quienes lideran el diseño académico y la estrategia
                  empresarial de la organización
                </p>
              </div>
            </FadeIn>

            <StaggeredGrid
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              staggerDelay={0.1}
            >
              {teamMembers.map((member) => (
                <motion.div
                  key={member.id}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6 text-center">
                      {member.photoUrl ? (
                        <img
                          src={member.photoUrl}
                          alt={member.name}
                          className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-primary-100 flex items-center justify-center">
                          <Users className="h-12 w-12 text-primary-600" />
                        </div>
                      )}
                      <h3 className="font-semibold text-lg text-secondary-900">
                        {member.name}
                      </h3>
                      <p className="text-sm text-primary-600 font-medium mb-2">
                        {ROLE_LABELS[member.role] || member.title}
                      </p>
                      <p className="text-sm text-secondary-600 line-clamp-3 mb-4">
                        {member.bio}
                      </p>
                      {member.linkedInUrl && (
                        <a
                          href={member.linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                        >
                          <Linkedin className="h-4 w-4" />
                          LinkedIn
                        </a>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </StaggeredGrid>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="container">
          <FadeIn>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                Comienza a transformar tu equipo hoy
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Descubre cómo ARCUS Learning puede ayudarte a alcanzar tus
                objetivos de desarrollo profesional y organizacional.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/courses">
                  <Button
                    size="lg"
                    className="bg-white text-primary-700 hover:bg-white/90"
                  >
                    Ver cursos disponibles
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                  >
                    Crear cuenta gratis
                  </Button>
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
